
import { defineComponent, ref, watch, onMounted } from 'vue';
import axios, { AxiosError } from 'axios';
import useAlert from "@/composables/Alert"
import router from '@/router';
import {
  CampHeader,
  CampFormHeader,
  CampFormRackSubmitBtn,
  CampTable,
  CampTableTh,
  CampTableTd,
  CampEmptyListFeedbackV2
} from '@/components';
import { campHandleEmptyFields } from '@/composables/DataValidation';
import { useLoaderStore } from "@/store/LoaderStore";
import { useCompanyStore } from '@/store/CompanyStore';
import { useStoreStore } from '@/store/StoreStore';
import { useAuthStore } from '@/store/AuthStore';
import ptBr from "element-plus/lib/locale/lang/pt-br";
import moment from 'moment';
import { TOptions, deleteMediaFromClient, uploadMediaFromClient } from '@/services/AzureBlobService';
import ModalQuiz, { TQuestion, createAlternative } from '@/views/Training/Components/ModalQuiz.vue';
import { useRoute } from 'vue-router';
import { getFileNameFromBlobUrl } from '../Social/ListPage.vue';
import { useFilterMainStore } from '@/store/FilterMainStore';
import MediaCardCore from '@/views/Training/Components/MediaCardCore.vue';
import SignatureInput from '@/components/SignatureInput.vue';
import SignatureModal from '@/views/Training/Components/SignatureModal.vue';
import type { ISignatureData } from '@/views/Training/Interfaces/ISignatureData';

type TYPE_TRAINING_GROUPS_OPTIONS = {
  COMPANY: {
    KEY: 1;
    LABEL: string;
  };
  STORE: {
    KEY: 2;
    LABEL: string;
  };
  COMPETITOR: {
    KEY: 3;
    LABEL: string;
  };
};

const TRAINING_GROUPS_OPTIONS: TYPE_TRAINING_GROUPS_OPTIONS = {
  COMPANY: {
    KEY: 1,
    LABEL: "Companhia"
  },
  STORE: {
    KEY: 2,
    LABEL: "Loja"
  },
  COMPETITOR: {
    KEY: 3,
    LABEL: "Competidor"
  }
}

type TTrainingGroups = 1 | 2 | 3

type TOption = { key: string | number | null, label: string }

function initQuestion():TQuestion {
  return {
    // id: null,
    title: '',
    points: 1,
    alternatives: [ createAlternative() ]
  }
}

interface ITrainingData {
  id?: number,
  title: string,
  description: string,
  url: string | null,
  active: 0 | 1,
  points: number,
  initial_date: string,
  end_date: string
  id_user: number | string
  id_company: number[] | string[] | null,
  id_store: number[] | string[] | null,
  id_competitor: number[] | string[] | null,
  // Certificate fields (match PostPage)
  certificate_responsible_name?: string,
  certificate_responsible_company?: string,
  certificate_enabled?: 0 | 1,
  certificate_workload?: number | null,
  certificate_logo?: string | null,
  certificate_responsible_position?: string | null,
  certificate_responsible_signature?: string | null,
}

interface ITrainingResponse {
  data: {
    error: boolean,
    message: string,
    data: any
    errorCode: string
  },
  status: number
}

interface ITrainingQuestionResponse {
  data: {
    error: boolean,
    message: string,
    data: any
    errorCode: string
  },
  status: number
}

interface ICreateTrainingResponse {
  data: {
    error: boolean,
    message: string,
    data: any
    errorCode: string
  },
  status: number
}

export interface IMediaFile {
  file: File | null
  URL: string | null
  youtubeURL: string | null
  contentType: string
  blobName: string
  URLFromParent: string | null
}

export const initMediaFile = {
  URL: null,
  youtubeURL: null,
  file: null,
  contentType: "",
  blobName: "",
  URLFromParent: ""
}

export default defineComponent({
  name: "TrainingPost",
  components: {
    CampHeader,
    CampFormHeader,
    CampFormRackSubmitBtn,
    ModalQuiz,
    CampTable,
    CampTableTh,
    CampTableTd,
    CampEmptyListFeedbackV2,
    MediaCardCore,
    SignatureInput,
    SignatureModal
  },
  setup() {
    // Variables
    const filterMainStore = useFilterMainStore()
    const isLoading = ref(true)
    const route = useRoute()
    const idRoute = route.params.id
    const { showTimeAlert } = useAlert()
    const loaderStore = useLoaderStore()
    const isRequiredField = ref(false)
    const infoMaxCharacters = ref<string | null>(null)
    const infoMaxCharactersWarning = ref<string | null>(null)
    const quizModalToggle = ref(false)
    const signatureModalToggle = ref(false)
    const savedSignatureData = ref<ISignatureData | null>(null)
    /** Company */
    const companyStore = useCompanyStore()
    /** Store */
    const storeStore = useStoreStore()
    /** User */
    const authStore = useAuthStore()
    /** Training */
    const activeStatus = ref(false)
    const certificateIssueStatus = ref(false)
    const trainingGroupsId = ref<TTrainingGroups>(authStore.isAdmUser ? TRAINING_GROUPS_OPTIONS.COMPANY.KEY : TRAINING_GROUPS_OPTIONS.STORE.KEY)
    const trainingGroupsList = [
      { key: TRAINING_GROUPS_OPTIONS.STORE.KEY, label: TRAINING_GROUPS_OPTIONS.STORE.LABEL },
      { key: TRAINING_GROUPS_OPTIONS.COMPETITOR.KEY, label: TRAINING_GROUPS_OPTIONS.COMPETITOR.LABEL }
    ]
    const trainingGroupsOptions = ref(authStore.isAdmUser ? [ { key: TRAINING_GROUPS_OPTIONS.COMPANY.KEY, label: TRAINING_GROUPS_OPTIONS.COMPANY.LABEL }, ...trainingGroupsList ] : [ ...trainingGroupsList ])
    const companyOptions = ref<TOption[]>([ { key: "", label: "" } ])
    const storeOptions: typeof companyOptions = ref([{ key: "", label: "" }])
    const competitorOptions: typeof companyOptions = ref([{ key: "", label: "" }])
    const currentDate = new Date()
    const maxDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate())
    const mediaFile = ref<IMediaFile>({ ...initMediaFile })
    const certificateLogoPreview = ref<string | null>(null)
    const certificateLogoFile = ref<File | null>(null)
    const INIT_DATA: ITrainingData = {
      title: "",
      points: 10,
      description: "",
      url: "",
      initial_date: moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end_date: moment().endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      active: activeStatus.value ? 1 : 0,
      certificate_responsible_name: "",
      certificate_enabled: 0,
      certificate_workload: null,
      certificate_logo: null,
      certificate_responsible_position: "",
      certificate_responsible_company: "",
      certificate_responsible_signature: "",
      id_company: null,
      id_store: null,
      id_competitor: null,
      id_user: authStore.getUser ? authStore.getUser.id : ""
    }
    const formModel = ref<ITrainingData>(INIT_DATA)
    const formModelAux = ref<ITrainingData>({
        title: '',
        description: '',
        url: null,
        active: 0,
        points: 0,
        initial_date: '',
        end_date: '',
        id_user: '',
        id_company: null,
        id_store: null,
        id_competitor: null
    })
    const urlMediaBackup = ref<string | null>(null)
    /** Question */
    const question = ref<TQuestion >(initQuestion())
    const questionList = ref<TQuestion[]>([])
    const editQuestionId = ref(-1)

    const handleCertificateLogoChange = (event: Event) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]
      if (!file) {
        return
      }

      const allowedTypes = ['image/png', 'image/jpeg']
      if (!allowedTypes.includes(file.type)) {
        showTimeAlert('Formato inválido. Envie uma imagem JPG ou PNG.', 'error')
        target.value = ''
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        showTimeAlert('O logo deve ter no máximo 5 MB.', 'error')
        target.value = ''
        return
      }

      certificateLogoFile.value = file
      certificateLogoPreview.value = URL.createObjectURL(file)
      formModel.value.certificate_logo = ''
    }

    const removeCertificateLogoFile = () => {
      certificateLogoFile.value = null
      certificateLogoPreview.value = null
      formModel.value.certificate_logo = ''
    }

    // Functions
    async function getTrainingView() {
      loaderStore.open()
      try {
        const [response1, response2, response3] = await Promise.all([
          axios.get(`/api/getTrainingView?id=${idRoute}`),
          axios.get(`/api/getTrainingQuestionsList?id_training=${idRoute}`),
          axios.get(`/api/getTrainingGroupsByIdTraining?id_training=${idRoute}`)
        ]);

        const { active, description, id_company, id_store, id_competitor, title, url, initial_date, end_date, id_user, id, points,
          certificate_responsible_name, certificate_enabled, certificate_workload, certificate_logo, certificate_responsible_position, certificate_responsible_company, certificate_responsible_signature
        } = response1.data.data
        urlMediaBackup.value = url
        formModel.value = {
          id,
          active,
          description,
          title,
          url,
          initial_date,
          end_date,
          id_user,
          points,
          id_company: null,
          id_store,
          id_competitor,
          certificate_responsible_name: certificate_responsible_name ?? "",
          certificate_enabled: typeof certificate_enabled !== 'undefined' ? certificate_enabled : 0,
          certificate_workload: certificate_workload ?? 0,
          certificate_logo: certificate_logo ?? "",
          certificate_responsible_position: certificate_responsible_position ?? "",
          certificate_responsible_company: certificate_responsible_company ?? "",
          certificate_responsible_signature: certificate_responsible_signature ?? "",
        }
        
        if (certificate_responsible_signature) {
          savedSignatureData.value = {
            name: certificate_responsible_name ?? "",
            role: certificate_responsible_position ?? "",
            company: certificate_responsible_company ?? "",
            signature: certificate_responsible_signature
          }
        }
        certificateLogoPreview.value = formModel.value.certificate_logo || null;
        activeStatus.value = active ? true : false
        // initialize switch from loaded model
        certificateIssueStatus.value = formModel.value.certificate_enabled === 1
        mediaFile.value.URLFromParent = url ? url : ""
        questionList.value = response2.data.data.map((el: TQuestion) => ({
          id: el.id,
          title: el.title,
          points: el.points,
          alternatives: el.alternatives.map(el => ({ ...el, del: false })),
          del: false
        }))

        const { data } = response3.data
        if(data.some(el => el.id_company)) {
          trainingGroupsId.value = TRAINING_GROUPS_OPTIONS.COMPANY.KEY
          formModelAux.value.id_company = data.map(el => el.id_company)
        } else if(data.some(el => el.id_store)) {
          trainingGroupsId.value = TRAINING_GROUPS_OPTIONS.STORE.KEY
          formModelAux.value.id_store = data.map(el => el.id_store)
        } else {
          trainingGroupsId.value = TRAINING_GROUPS_OPTIONS.COMPETITOR.KEY
          try {
            const response = await axios.get(`/api/getCompetitorTrainingListByIdTraining?id_training=${formModel.value.id}`)
            const { data } = response.data
            formModelAux.value.id_competitor = data.map(el => el.id_competitor)
          } catch (error) {
            if(error instanceof AxiosError) {
              if(error.response)
                showTimeAlert(error.response?.data.message, "error")
              else
                showTimeAlert("Algo deu errado, tente novamente mais tarde", "error")
            } else {
              showTimeAlert("Algo deu errado, tente novamente mais tarde", "error")
            }
          }
        }
      } catch (error) {
        if(error instanceof AxiosError) {
          if(error.response)
            showTimeAlert(error.response?.data.message, "error")
          else
            showTimeAlert("Algo deu errado, tente novamente mais tarde", "error")
        } else {
          showTimeAlert("Algo deu errado, tente novamente mais tarde", "error")
        }
      } finally {
        loaderStore.close()
      }
    }

    async function getTrainingGroups() {
      isLoading.value = true
      try {
        if(trainingGroupsId.value === TRAINING_GROUPS_OPTIONS.COMPANY.KEY) {
          const response = await axios.get(`/api/getCompanyList`)
          const { data } = response.data
          companyOptions.value = [ ...data.map(el => ({ key: el.id, label: el.fantasy_name })) ]
          if(Array.isArray(formModelAux.value.id_company) && formModelAux.value.id_company.length) {
            formModelAux.value.id_company.forEach((id: string | number | null) => {
              const ID = +id!
              companyOptions.value = companyOptions.value.filter(company => company.key !== ID)
            })
          }
        }
        if(trainingGroupsId.value === TRAINING_GROUPS_OPTIONS.STORE.KEY) {
          const response = await axios.get(`/api/getStoreList`)
          const { data } = response.data
          storeOptions.value = [ ...data.map(el => ({ key: el.id, label: el.fantasy_name + ' - ' + el.cnpj })) ]
          if(Array.isArray(formModelAux.value.id_store) && formModelAux.value.id_store.length) {
            formModelAux.value.id_store.forEach((id: string | number | null) => {
              const ID = +id!
              storeOptions.value = storeOptions.value.filter(store => store.key !== ID)
            })
          }
        }
        if(trainingGroupsId.value === TRAINING_GROUPS_OPTIONS.COMPETITOR.KEY) {
          const response = await axios.get(`/api/getCompetitor`)
          const { data } = response.data
          competitorOptions.value = [ ...data.map(el => ({ key: el.id, label: el.name })) ]
          if(Array.isArray(formModelAux.value.id_competitor) && formModelAux.value.id_competitor) {
            formModelAux.value.id_competitor.forEach((id: string | number | null) => {
              const ID = id!.toString()
              competitorOptions.value = competitorOptions.value.filter(competitor => competitor.key !== ID)
            })
          }
        }
        isLoading.value = false
      } catch (error) {
        if(error instanceof AxiosError) {
          if(error.response)
            showTimeAlert(error.response?.data.message, "error")
          else
            showTimeAlert("Algo deu errado, tente novamente mais tarde", "error")
        } else {
          showTimeAlert("Algo deu errado, tente novamente mais tarde", "error")
        }
      }
    }

    const resetWarning = () => isRequiredField.value = false

    /** Date */
    const handleDisabledDateMin = (time: Date) => time.getTime() < maxDate.getTime()

    const handleDisabledDateMax = (time: Date) => time.getTime() < moment(formModel.value.initial_date).valueOf()

    const handleFixRangeDate = () => {
      if(moment(formModel.value.initial_date).valueOf() >= moment(formModel.value.end_date).valueOf())
        formModel.value.end_date = moment(formModel.value.initial_date).add(1, 'hour').format()
      if(!formModel.value.initial_date || !formModel.value.end_date) {
        formModel.value.initial_date = moment().startOf('day').format('YYYY-MM-DD HH:mm:ss')
        formModel.value.end_date = moment().endOf('day').format('YYYY-MM-DD HH:mm:ss')
      }
    }

    /** Size Desc */
    function handleSizeDesc(max = 255) {
      const length = formModel.value.description.length
      infoMaxCharacters.value = `${length}/${max}`
      if(length > max)
        infoMaxCharactersWarning.value = `Máximo de ${max} caracteres atingido!`
      else
        infoMaxCharactersWarning.value = null
    }

    /** Handle Open Media Modal */
    const handleQuizModalToggle = () => quizModalToggle.value = !quizModalToggle.value

    const handleSignatureModalToggle = () => {
      signatureModalToggle.value = !signatureModalToggle.value
    }

    function handleSaveSignature(signatureData: ISignatureData){
      formModel.value.certificate_responsible_name = signatureData.name
      formModel.value.certificate_responsible_position = signatureData.role
      formModel.value.certificate_responsible_company = signatureData.company
      formModel.value.certificate_responsible_signature = signatureData.signature
      savedSignatureData.value = signatureData
    }

    const resetQuizModal = () => {
      question.value = initQuestion()
      editQuestionId.value = -1
    }

    const handleQuestion = (value: { question: TQuestion, editQuestionId: number }) => {
      if(value.editQuestionId < 0) {
        questionList.value.push(value.question)
      }
      else {
        questionList.value[value.editQuestionId] = value.question
        resetQuizModal()
      }
    }

    const handleEditQuestion = (edit: TQuestion, id: number) => {
      question.value = edit
      editQuestionId.value = id
      handleQuizModalToggle()
    }

    const handleRemoveQuestion = (i: number) => {
      resetQuizModal()
      questionList.value = questionList.value.map((el, j) => (i !== j ? { ...el } : { ...el, del: true }))
    }

    const handleUndoRemoveQuestion = (i: number) => {
      resetQuizModal()
      questionList.value = questionList.value.map((el, j) => (i !== j ? { ...el } : { ...el, del: false }))
    }

    async function handleCompetitorTraining(id_training: number) {
      let id_company: number[] | string[] | null = null
      let id_store: number[] | string[] | null = null
      let id_competitor: number[] | string[] | null = null

      if(trainingGroupsId.value === TRAINING_GROUPS_OPTIONS.COMPANY.KEY)
        id_company = formModel.value.id_company

      if(trainingGroupsId.value === TRAINING_GROUPS_OPTIONS.STORE.KEY)
        id_store = formModel.value.id_store
      
      if(trainingGroupsId.value === TRAINING_GROUPS_OPTIONS.COMPETITOR.KEY)
        id_competitor = formModel.value.id_competitor

      const bodyToPost = {
        id_training,
        id_company,
        id_store,
        id_competitor
      }

      if(
        (id_company && Array.isArray(id_company) && id_company.length)
        ||
        (id_store && Array.isArray(id_store) && id_store.length)
        ||
        (id_competitor && Array.isArray(id_competitor) && id_competitor.length)
      ) {
        try {
          const response: ICreateTrainingResponse = await axios.post(`/api/postCompetitorTraining`, bodyToPost)
        } catch (error) {
          if(error instanceof AxiosError) {
            if(error.response)
              showTimeAlert(error.response?.data.message, "error")
            else
              showTimeAlert("Algo deu errado, tente novamente mais tarde", "error")
          } else {
            showTimeAlert("Algo deu errado, tente novamente mais tarde", "error")
          }
        }
      }
    }

    // const helperDelFileClowd = async () => {
    //   if(typeof urlMediaBackup.value !== "string")
    //     return
    //   const url = getFileNameFromBlobUrl(urlMediaBackup.value)
    //   const lastIndex = url.lastIndexOf("/");
    //   const blobName = url.substring(lastIndex + 1);

    //   let folder = ""
    //   if(mediaFile.value.contentType.startsWith('image/'))
    //     folder = "image/"
    //   if(mediaFile.value.contentType.startsWith('video/'))
    //     folder = "video/"
    //   if(mediaFile.value.contentType.startsWith('application/pdf'))
    //     folder = "application/"

    //   await deleteMediaFromClient(`training/${folder}${blobName}`)
    // }

    const helperDelFileClowd = async () => {
      if (typeof urlMediaBackup.value !== "string") return;

      const url = getFileNameFromBlobUrl(urlMediaBackup.value);

      const lastIndex = url.lastIndexOf("/");
      const blobName = url.substring(lastIndex + 1);

      let folder = "";

      if (mediaFile.value.contentType.startsWith("image/"))
        folder = "image/";

      if (mediaFile.value.contentType.startsWith("video/"))
        folder = "video/";

      if (mediaFile.value.contentType.startsWith("application/pdf"))
        folder = "application/";

      const fullBlobName = `training/${folder}${blobName}`;

      await deleteMediaFromClient(fullBlobName);
    }

    async function onSubmitForm() {
      isRequiredField.value = false

      if(infoMaxCharactersWarning.value){
        showTimeAlert("Verifique os campos obrigatórios.", "error")
        return true
      }

      formModel.value.initial_date = moment(formModel.value.initial_date).format('YYYY-MM-DD HH:mm:ss')
      formModel.value.end_date = moment(formModel.value.end_date).format('YYYY-MM-DD HH:mm:ss')
      formModel.value.active = activeStatus.value ? 1 : 0
      formModel.value.certificate_enabled = certificateIssueStatus.value ? 1 : 0
      const {id_company, id_store, id_competitor, id, url, certificate_logo, certificate_responsible_name, certificate_responsible_company, certificate_enabled, certificate_workload, certificate_responsible_position, certificate_responsible_signature, ...requiredFields } = formModel.value

      if(campHandleEmptyFields(requiredFields)) {
        showTimeAlert("Verifique os campos obrigatórios.", "error")
        isRequiredField.value = true
        return true
      }

      if (certificateIssueStatus.value) {
        if (!formModel.value.certificate_workload) {
          showTimeAlert("Carga horária do certificado é obrigatória.", "error")
          return true
        }

        if (
          !formModel.value.certificate_logo &&
          !certificateLogoFile.value
        ) {
          showTimeAlert("Logo do certificado é obrigatória.", "error")
          return true
        }

        if (!formModel.value.certificate_responsible_name?.trim()) {
          showTimeAlert("Nome do responsável pelo certificado é obrigatório.", "error")
          return true
        }

        if (!formModel.value.certificate_responsible_company?.trim()) {
          showTimeAlert("Nome da empresa responsável pelo certificado é obrigatório.", "error")
          return true
        }

        if (!formModel.value.certificate_responsible_position?.trim()) {
          showTimeAlert("Cargo do responsável pelo certificado é obrigatório.", "error")
          return true
        }

        if (!formModel.value.certificate_responsible_signature) {
          showTimeAlert("Assinatura do responsável pelo certificado é obrigatória.", "error")
          return true
        }
      }
      
      if(mediaFile.value.file) {
        const options: TOptions = {
          blobHTTPHeaders: {
            blobContentType: mediaFile.value.contentType
          }
        };
        try {
          loaderStore.open();

          const { request } = (
            await uploadMediaFromClient(
              mediaFile.value.file,
              mediaFile.value.blobName,
              options
            )
          )._response;

          formModel.value.url = request.url;

          try {
            await helperDelFileClowd();
          } catch (e) {
            console.warn("Não foi possível excluir o blob antigo.", e);
          }

        } catch (error) {
          loaderStore.close();
          return showTimeAlert("Erro ao salvar mídia, tente mais tarde!", "error");
        }
      }

      if (certificateLogoFile.value) {
        try {

          const blobName = `certificate/logo/${Date.now()}-${certificateLogoFile.value.name}`;

          const options: TOptions = {
            blobHTTPHeaders: {
              blobContentType: certificateLogoFile.value.type
            }
          };

          const logoResponse = await uploadMediaFromClient(
            certificateLogoFile.value,
            blobName,
            options
          );
          
          const logoUrl = `https://${process.env.VUE_APP_AZURE_STORAGE_NAME}.blob.core.windows.net/${process.env.VUE_APP_AZURE_STORAGE_CONTAINER}/${blobName}`;

          formModel.value.certificate_logo = logoUrl;

        } catch (error) {
          console.log(error);
        }
      }

      const bodyToUpdate = {
        url: formModel.value.url,
        id_company,
        id_training: idRoute,

        certificate_enabled: formModel.value.certificate_enabled,
        certificate_workload: formModel.value.certificate_workload,
        certificate_logo: formModel.value.certificate_logo,
        certificate_responsible_name: formModel.value.certificate_responsible_name,
        certificate_responsible_company: formModel.value.certificate_responsible_company,
        certificate_responsible_position: formModel.value.certificate_responsible_position,
        certificate_responsible_signature: formModel.value.certificate_responsible_signature,

        ...requiredFields,
        questionList: questionList.value
      }

      if(!mediaFile.value.URL) {
        try {
          loaderStore.open()
          helperDelFileClowd()
        } catch (error) {
          loaderStore.close()
          return showTimeAlert('Erro ao excluir media, tente mais tarde!', 'error')
        }
      }

      /** Exclui URL no Banco do Blob removido na Nuvem */
      if(!mediaFile.value.URL)
        bodyToUpdate.url = null

      /** Sobrepõe URL antiga para atualizar */
      if(mediaFile.value.youtubeURL)
        bodyToUpdate.url = mediaFile.value.youtubeURL

      try {
        loaderStore.open()
        const response: ITrainingResponse = await axios.put(`/api/putTraining`, { ...bodyToUpdate })
        const idTraining = +(Array.isArray(bodyToUpdate.id_training) ? bodyToUpdate.id_training[0] : bodyToUpdate.id_training)
        handleCompetitorTraining(idTraining)
        showTimeAlert("Treinamento atualizado com sucesso!")
        // formModel.value = { ...INIT_DATA }
        router.push("/treinamentos")
      } catch (error) {
        if(error instanceof AxiosError) {
          if(error.response) {
            showTimeAlert(error.response?.data.message, "error")
          }else {
            showTimeAlert("Algo deu errado, tente novamente mais tarde", "error")
          }
        } else {
          showTimeAlert("Algo deu errado, tente novamente mais tarde", "error")
        }
        loaderStore.close()
      }
    }

    const handleUpdatedMediaFile = (emittedObject: IMediaFile) => mediaFile.value = emittedObject

    // /** Handle Filter Modal */
    // function handleInitFilterModal() {
    //   if(!companyStore.getId || !storeStore.getId) {
    //     filterMainStore.hideModal()
    //     filterMainStore.showModal()
    //   }
    // }

    // Life cycles - Actions
    onMounted(async() => {
      // handleInitFilterModal()
      await getTrainingView()
      await getTrainingGroups()
    })

    watch(() => { trainingGroupsId.value, companyStore.getId }, async () => await getTrainingGroups(), { deep: true })

    watch(() => formModel.value.description, () => handleSizeDesc())

    watch(() => { formModel.value.initial_date, formModel.value.end_date }, () => handleFixRangeDate(), { deep: true })

    return {
      isRequiredField,
      isLoading,
      companyStore,
      storeStore,
      resetWarning,
      formModel,
      certificateIssueStatus,
      infoMaxCharacters,
      infoMaxCharactersWarning,
      trainingGroupsId,
      trainingGroupsOptions,
      activeStatus,
      ptBr,
      TRAINING_GROUPS_OPTIONS,
      companyOptions,
      storeOptions,
      competitorOptions,
      mediaFile,
      handleUpdatedMediaFile,
      handleCertificateLogoChange,
      removeCertificateLogoFile,
      certificateLogoPreview,
      handleDisabledDateMin,
      handleDisabledDateMax,
      onSubmitForm,
      handleQuizModalToggle,
      handleSignatureModalToggle,
      handleSaveSignature,
      signatureModalToggle,
      savedSignatureData,
      quizModalToggle,
      question,
      questionList,
      editQuestionId,
      resetQuizModal,
      handleQuestion,
      handleEditQuestion,
      handleRemoveQuestion,
      handleUndoRemoveQuestion,
      moment,
    }
  }
})
